// SS PWA — pages/ClientHealth.tsx — 2026-06-03
//
// CLIENT-FACING OWNER VIEW. Per-tenant only. Shows outcomes, not internals.
// Gated to role=owner; scoped to the URL slug.
//
// Route: /:slug/health
//
// LOCKED RULES (per spec):
//   - No rule names ever (use copy/healthOutcomeCopy.ts).
//   - No cache/429/502/KV/D1 anything.
//   - No other tenants' data.
//   - "We caught X and fixed it automatically" framing.

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { HealthStatusBanner } from '@/components/health/HealthStatusBanner';
import { WeeklyOutcomesGrid } from '@/components/health/WeeklyOutcomesGrid';
import {
  fetchAudit,
  fetchHealth,
  type AuditResponse,
  type HealthResponse,
  DiagnosticsApiError,
} from '@/lib/api/diagnostics';
import { healthCopy, issueDigestFor } from '@/lib/copy/healthOutcomeCopy';
import { RoleGate } from '@/components/RoleGate';

function ClientHealthInner() {
  const { slug = '' } = useParams<{ slug: string }>();
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [audit, setAudit] = useState<AuditResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    void Promise.all([fetchHealth(slug), fetchAudit(slug, { hours: 168 })])
      .then(([h, a]) => {
        if (cancelled) return;
        setHealth(h);
        setAudit(a);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof DiagnosticsApiError) {
          setError(healthCopy.empty.error);
        } else {
          setError(healthCopy.empty.error);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (error) {
    return (
      <div className="mx-auto max-w-3xl space-y-4 p-4">
        <div className="rounded-2xl border border-ink-200 bg-white p-6 text-center text-sm text-ink-600 dark:border-[#1F2A37] dark:bg-[#0F172A] dark:text-[#94A3B8]">
          {error}
        </div>
      </div>
    );
  }

  if (!health || !audit) {
    return (
      <div className="mx-auto max-w-3xl space-y-4 p-4">
        <div className="rounded-2xl border border-ink-200 bg-white p-6 text-center text-sm text-ink-500 dark:border-[#1F2A37] dark:bg-[#0F172A] dark:text-[#94A3B8]">
          {healthCopy.empty.loading}
        </div>
      </div>
    );
  }

  // Count "issues we caught" without ever surfacing the rule names.
  const issuesCaught = audit.summary.anomalies.total;

  return (
    <div className="mx-auto max-w-3xl space-y-4 p-4">
      <header>
        <h1 className="text-xl font-bold text-ink-900 dark:text-white">
          {health.tenant.display_name}
        </h1>
        <div className="text-sm text-ink-500 dark:text-[#94A3B8]">
          Your week at a glance · updated {new Date(audit.generated_at).toLocaleTimeString()}
        </div>
      </header>

      <HealthStatusBanner status={health.status} />

      <WeeklyOutcomesGrid audit={audit} />

      <section className="rounded-2xl border border-ink-200 bg-white p-4 dark:border-[#1F2A37] dark:bg-[#0F172A]">
        <div className="text-sm font-medium text-ink-700 dark:text-[#94A3B8]">
          Behind the scenes
        </div>
        <div className="mt-1 text-base font-semibold text-ink-900 dark:text-white">
          {issueDigestFor(issuesCaught)}
        </div>
        <div className="mt-1 text-xs text-ink-500 dark:text-[#94A3B8]">
          Anything that needed attention was handled — you don't need to do anything.
        </div>
      </section>

      <footer className="pt-4 text-xs text-ink-400">
        Service Steady · always-on receptionist
      </footer>
    </div>
  );
}

export function ClientHealth() {
  // 2026-06-03 (diagnostics packet — client-page deprecation) — clients
  // don't get the per-tenant /:slug/health page yet. The simple stats
  // widget on their existing pages stays as their visible health surface.
  // This route is now operator-only (was 'owner' | 'ss_operator') so
  // we preserve the component + design for the future GPT stats redesign,
  // but a client navigating here gets the access-denied panel from RoleGate.
  return (
    <RoleGate required="ss_operator">
      <ClientHealthInner />
    </RoleGate>
  );
}
